import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { useMutation, useQuery } from '@hooks/reactQuery';

import { auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';

import { uploadFileToGcloud } from '@api/common';
import {
	approveImport,
	getAllFields,
	getFieldSamples,
	getImportStatistic,
	getImportTypes,
	getSampleUrl,
	getSystemFields,
	getUploadURL,
	runAllFields,
	runFieldSamples,
	runImportStatistic,
	submitFieldsMapping,
	submitImportRules,
} from '@api/importSites';

import { checkIsRequestAccepted, extractQueryParams, isEmpty } from '@utils/common';
import { fileSaver } from '@utils/fileSaver';
import { fieldsOptionsFormatted, optionsFormatted, optionsFormattedByName } from '@utils/formatter';
import { handleErrorFromMessage } from '@utils/handleRequestError';
import { generateHeaders } from '@utils/headersMapper';

import { IVendor } from '@interfaces/cmModule/dto/cmModuleDTO';
import { TRequiredPick } from '@interfaces/common/genericWithModifying';
import { IResponseError } from '@interfaces/errors/apiErrors';
import { IFieldsMappingDTO, ISaveImportRulesDTO } from '@interfaces/import/dto/importDTO';
import { IFields, IFieldsSamples, IImportStatisticsProps, IUploadUrlType } from '@interfaces/import/import';

import { ESnackbarTypes } from '@enums/common';
import { EImportingDataType } from '@enums/dropzone';

import { refetchOptions } from '@constants/common';
import {
	ALL_CELLS,
	ALL_FIELDS_SITES,
	DOWNLOAD_SITES_SAMPLE,
	FIELD_SITES_SAMPLES,
	IMPORT_SITES_EXECUTE,
	IMPORT_SITES_STATISTIC,
	IMPORT_SITES_TYPES,
	SAVE_IMPORT_SITES_RULES,
	SAVE_SITES_FIELDS_MAPPING,
	SITES_DB,
	SITES_SYSTEM_FIELDS,
	SITES_UPLOAD_URL,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';
import {
	REFETCH_STATISTICS_INTERVAL,
} from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/constants';

interface UseImportSitesParams extends IImportStatisticsProps, IVendor, IFields {
}

type TUseImportSites = IImportStatisticsProps & IVendor<string> & IFields;

export const useImportSites = (props: TUseImportSites) => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();
	const {
		vendor,
		setImportIds,
		importId,
		closeModals,
		importType,
		fieldsTechnology,
		isResultStatisticComponent,
		isImportRulesComponent,
		isFieldsMappingComponent,
	} = props as TRequiredPick<UseImportSitesParams, 'importId'>;
	const { accessPermission: { networkOperatorTelecomSitesAttributeUpdater } } = useAppSelector(auth);

	const [refetchIntervalImportStatistic, setRefetchIntervalImportStatistic]
		= useState<number | false>(REFETCH_STATISTICS_INTERVAL);
	const [refetchIntervalAllFields, setRefetchIntervalAllFields]
		= useState<number | false>(REFETCH_STATISTICS_INTERVAL);
	const [refetchIntervalFieldsSamples, setRefetchIntervalFieldsSamples]
		= useState<number | false>(REFETCH_STATISTICS_INTERVAL);

	const resetIntervals = () => {
		setRefetchIntervalImportStatistic(REFETCH_STATISTICS_INTERVAL);
		setRefetchIntervalAllFields(REFETCH_STATISTICS_INTERVAL);
		setRefetchIntervalFieldsSamples(REFETCH_STATISTICS_INTERVAL);
	};

	const {
		mutate: createSitesUploadUrl,
		data: uploadUrlData,
		isLoading: isLoadingCreateUploadUrl,
	} = useMutation<IUploadUrlType>({
		mutationKey: [SITES_UPLOAD_URL],
		mutationFn: ({ files }: { files: Array<File> }) => {
			resetIntervals();

			getUploadURL({ fileName: files[0].name, technology: fieldsTechnology, vendor })
				.then((data) => {
					const headers = generateHeaders(data?.headers);
					const url = data?.uploadUrl;

					uploadFileToGcloud({ url, data: files[0], headers })
						.then(() => {
							if (data?.importId) setImportIds([data.importId]);
						})
						.then(() => {
							if (data?.importId) runAllFields({ id: data.importId, vendor })
								.catch((err) => {
									dispatch(setSnackbar({
										message: err?.message || responseMessages.error,
										type: ESnackbarTypes.error,
									}));

								});
						})
						.catch((err) => {
							dispatch(setSnackbar({
								message: err?.message || responseMessages.error,
								type: ESnackbarTypes.error,
							}));
						});
				})
				.catch(err => handleErrorFromMessage(err));
		},
	});

	const {
		mutate: downloadSample,
		data: sitesSampleUrl,
	} = useMutation({
		mutationKey: [DOWNLOAD_SITES_SAMPLE],
		mutationFn: () => {
			getSampleUrl(vendor)
				.then((data) => {
					if (data?.downloadUrl) fileSaver(data.downloadUrl);
				})
				.catch(err => {
					dispatch(setSnackbar({
						message: err?.message || responseMessages.error,
						type: ESnackbarTypes.error,
					}));
				});
		},
	});

	const {
		mutate: saveImportRules,
		isLoading: isLoadingSitesImportRules,
	} = useMutation<ISaveImportRulesDTO & { nextClickHandler: () => void }>({
		mutationKey: [SAVE_IMPORT_SITES_RULES, importId],
		mutationFn: (data: ISaveImportRulesDTO & { nextClickHandler: () => void }) =>
			submitImportRules({ ...data, vendor })
				.then(() => {
					runFieldSamples({ id: importId, vendor })
						.catch((err) => {
							dispatch(setSnackbar({
								message: err?.message || responseMessages.error,
								type: ESnackbarTypes.error,
							}));
						});
				})
				.then(() => {
					data.nextClickHandler();
				})
				.catch(err => {
					dispatch(setSnackbar({
						message: err?.message || responseMessages.error,
						type: ESnackbarTypes.error,
					}));
				}),
	});

	const {
		data: sitesFieldsSamples,
		isLoading: isLoadingFieldsSamples,
		isFetching: isFetchingFieldsSamples,
	} = useQuery<AxiosResponse, {
		data: Array<IFieldsSamples>;
		status: number;
	}>({
		queryKey: [FIELD_SITES_SAMPLES, { id: importId, vendor }],
		queryFn: extractQueryParams(getFieldSamples),
		enabled: [!!importId, isFieldsMappingComponent],
		refetchInterval: refetchIntervalFieldsSamples,
		refetchOnWindowFocus: false,
		refetchOnmount: false,
		select: (response) => {
			const fields = !isEmpty(response.data) ? optionsFormattedByName(response.data) : [];

			return { data: fields as Array<IFieldsSamples>, status: response.status };
		},
		onSuccess: (data) => {
			if (checkIsRequestAccepted(data)) {
				setRefetchIntervalFieldsSamples(false);
			}
		},
	});

	const {
		mutate: saveSitesFieldsMapping,
		isLoading: isLoadingSaveFieldsMapping,
	} = useMutation<{
		fieldsMapping: Array<IFieldsMappingDTO>;
		nextClick: () => void;
	}>({
		mutationKey: [SAVE_SITES_FIELDS_MAPPING],
		mutationFn: (data: { fieldsMapping: Array<IFieldsMappingDTO>; nextClick: () => void }) =>
			submitFieldsMapping({ id: importId!, fieldsMapping: data.fieldsMapping, vendor })
				.then(() => {
					runImportStatistic({ id: importId!, vendor })
						.catch((err: IResponseError) => handleErrorFromMessage(err));
				})
				.then(() => {
					data.nextClick();
				})
				.catch((err: IResponseError) => handleErrorFromMessage(err)),
		...refetchOptions,
		enabled: [!!importId],
	});

	const {
		data: sitesImportStatistic,
		isLoading: isLoadingSitesImportStatistic,
		isFetching: isFetchingSitesImportStatistic,
	} = useQuery({
		queryKey: [IMPORT_SITES_STATISTIC, { id: importId, vendor }],
		queryFn: extractQueryParams(getImportStatistic),
		refetchInterval: refetchIntervalImportStatistic,
		refetchOnWindowFocus: false,
		refetchOnmount: false,
		onSuccess: (data: AxiosResponse) => {
			if (checkIsRequestAccepted(data)) {
				setRefetchIntervalImportStatistic(false);
			}
		},
		enabled: [!!importId, isResultStatisticComponent],
	});

	const {
		mutate: executeSitesImport,
		isLoading: isLoadingExecuteImport,
	} = useMutation({
		mutationKey: [IMPORT_SITES_EXECUTE],
		mutationFn: () => approveImport({ id: importId, vendor }),
		onSuccess: () => {
			closeModals();

			dispatch(setSnackbar({
				message: responseMessages.importSites,
				type: ESnackbarTypes.success,
			}));

			return [
				queryClient.invalidateQueries([SITES_DB]),
				queryClient.invalidateQueries([ALL_CELLS]),
			];
		},
		enabled: [!!importId],
	});

	const {
		data: sitesSystemFields,
		isLoading: isLoadingSystemFields,
		isFetching: isFetchingSystemFields,
	} = useQuery({
		queryKey: [SITES_SYSTEM_FIELDS, fieldsTechnology, importId],
		queryFn: () => getSystemFields({ id: importId, vendor }),
		...refetchOptions,
		enabled: [(importType === EImportingDataType.site), networkOperatorTelecomSitesAttributeUpdater, !!importId],
	});

	const {
		data: importTypes,
		isLoading: isLoadingImportTypes,
		isFetching: isFetchingImportTypes,
	} = useQuery({
			queryKey: [IMPORT_SITES_TYPES, vendor],
			queryFn: extractQueryParams(getImportTypes),
			select: (importTypes) => optionsFormatted(importTypes),
			...refetchOptions,
			enabled: [!!importId, (importType === EImportingDataType.site)],
		},
	);

	const {
		data: allFields,
		isLoading: isLoadingAllFields,
		isFetching: isFetchingAllFields,
	} = useQuery<AxiosResponse, any>({
			queryKey: [ALL_FIELDS_SITES, importId],
			queryFn: () => getAllFields({ id: importId, vendor }),
			select: (response) => {
				const fields = fieldsOptionsFormatted(response.data.allFields || []);

				return { data: fields, numRows: response.data.numRows, status: response.status };
			},
			enabled: [!!importId, (importType === EImportingDataType.site), isImportRulesComponent],
			refetchInterval: refetchIntervalAllFields,
			refetchOnWindowFocus: false,
			refetchOnmount: false,
			onSuccess: (data: AxiosResponse) => {
				if (checkIsRequestAccepted(data)) {
					setRefetchIntervalAllFields(false);
				}
			},
		},
	);

	return {
		allFields: allFields?.data || [],
		numRows: allFields?.numRows,
		sitesFieldsSamples: sitesFieldsSamples?.data || [],
		createSitesUploadUrl,
		downloadSample,
		executeSitesImport,
		sitesImportStatistic: sitesImportStatistic?.data || {},
		importTypes: importTypes || [],
		isLoadingSitesFieldsMapping: isLoadingSaveFieldsMapping
			|| (isLoadingFieldsSamples && isFetchingFieldsSamples)
			|| !checkIsRequestAccepted(sitesFieldsSamples),
		isLoadingSitesImportRules: isLoadingSitesImportRules || (isLoadingAllFields && isFetchingAllFields)
			|| !checkIsRequestAccepted(allFields),
		isLoadingSitesResultStatistic: (isLoadingSitesImportStatistic && isFetchingSitesImportStatistic)
			|| isLoadingExecuteImport
			|| !checkIsRequestAccepted(sitesImportStatistic),
		isUploadingSitesFile: isLoadingCreateUploadUrl || (isLoadingSystemFields && isFetchingSystemFields)
			|| (isLoadingImportTypes && isFetchingImportTypes),
		sampleUrl: sitesSampleUrl,
		saveSitesFieldsMapping,
		saveImportRules,
		sitesSystemFields: sitesSystemFields || [],
		uploadUrlData,
	};
};
